import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import chevronIcon from '../../icons/Icon-Chevron-Right-Green.svg';
import useLocalize from '../../utils/hooks/useLocalize';

// First character of the name (spread form so a surrogate pair counts as one),
// shown as a placeholder when the logo is missing.
const initialOf = (name) => (name ? [...name][0] : '');

// STORY-013: one candidate row in the results step — a selection checkbox and a
// card linking to the symbol detail screen. Presentational only; renders an
// `<li>`, so it must be mounted inside the results `<ul>`.
export const StockCard = ({
  symbolId,
  name,
  industry,
  description,
  selected,
  onToggle,
  logoUrl
}) => {
  const localize = useLocalize();
  const [logoFailed, setLogoFailed] = useState(false);

  // A new URL (e.g. after a token refresh) is a fresh attempt, so retry.
  useEffect(() => setLogoFailed(false), [logoUrl]);

  const showLogo = Boolean(logoUrl) && !logoFailed;

  return (
    <li className="ai-scanner-results-row">
      {/* Kept outside the anchor so ticking never navigates (Figma 5271:12106). */}
      <label className="ai-scanner-results-checkbox">
        <input
          type="checkbox"
          className="ai-scanner-results-checkbox-input"
          checked={selected}
          onChange={() => onToggle(symbolId)}
          aria-label={localize('ai.scanner.results.card.select', { name })}
        />
        <span className="ai-scanner-results-checkbox-mark" aria-hidden="true" />
      </label>
      {/* New tab so navigating away never unmounts the scanner conversation. */}
      <Link
        className="ai-scanner-results-card"
        to={`/symbol/${symbolId}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        <span className="ai-scanner-results-card-body">
          <span className="ai-scanner-results-card-heading">
            {showLogo ? (
              <img
                className="ai-scanner-results-card-logo"
                src={logoUrl}
                alt=""
                aria-hidden="true"
                width="38"
                height="38"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <span
                className="ai-scanner-results-card-logo ai-scanner-results-card-logo-fallback"
                aria-hidden="true"
              >
                {initialOf(name)}
              </span>
            )}
            <span className="ai-scanner-results-card-name">{name}</span>
          </span>
          {/* Omitted rather than left empty: the card body is a flex column
              with a gap, so a blank child still costs vertical space. */}
          {industry && (
            <span className="ai-scanner-results-card-industry">{industry}</span>
          )}
          <span className="ai-scanner-results-card-description">
            {description?.trim() ||
              localize('ai.scanner.results.card.description.placeholder')}
          </span>
        </span>
        <img
          className="ai-scanner-results-card-chevron"
          src={chevronIcon}
          alt=""
          aria-hidden="true"
          width="23"
          height="32"
        />
      </Link>
    </li>
  );
};

StockCard.propTypes = {
  symbolId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  // A missing industry omits the tag; a missing description shows the
  // placeholder.
  industry: PropTypes.string,
  description: PropTypes.string,
  selected: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  logoUrl: PropTypes.string
};

export default StockCard;
